import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, MapPin, Clock, Briefcase, Check, Send, Lock, AlertTriangle, Link2 } from "lucide-react";
import { toast } from "sonner";
import { orgNowLocal } from "@/lib/tz";

const fmtTime = (dt) => (dt ? (String(dt).split(" ")[1] || "").slice(0, 5) : "");
const dayLabel = (ds) => {
  if (!ds) return "";
  const d = new Date(ds + "T00:00:00");
  return d.toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" });
};
const daysUntil = (ds) => {
  if (!ds) return null;
  const d = new Date(ds + "T00:00:00");
  return Math.ceil((d - orgNowLocal().toDate()) / 86400000);
};

export default function WorkerJobBoard() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState("");
  const [confirmJob, setConfirmJob] = useState(null);

  const load = () =>
    api.workerJobBoard().then(setJobs).catch(() => toast.error("Could not load the job board.")).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const [clashJob, setClashJob] = useState(null);

  const requestApply = (s) => {
    // Already rostered at that time? Make them see it before applying.
    if ((s.clashes || []).length > 0) { setClashJob(s); return; }
    const d = daysUntil(s.shift_date);
    if (d != null && d < 30) { setConfirmJob(s); return; }
    apply(s.name);
  };

  const afterClash = (s) => {
    setClashJob(null);
    const d = daysUntil(s.shift_date);
    if (d != null && d < 30) { setConfirmJob(s); return; }
    apply(s.name);
  };

  const withdraw = async (name) => {
    setApplying(name);
    try {
      await api.workerUnapply(name);
      toast.success("Application withdrawn");
      setJobs((js) => js.map((j) => (j.name === name ? { ...j, applied: false } : j)));
    } catch (e) { toast.error(e.response?.data?.detail || "Could not withdraw"); }
    finally { setApplying(""); }
  };

  const apply = async (name) => {
    setConfirmJob(null);
    setApplying(name);
    try {
      await api.workerApply(name);
      toast.success("Applied — the office will review your request");
      setJobs((js) => js.map((j) => (j.name === name ? { ...j, applied: true } : j)));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not apply");
    } finally {
      setApplying("");
    }
  };

  if (loading)
    return <div className="flex justify-center pt-24"><Loader2 className="h-6 w-6 animate-spin text-[#00c86e]" /></div>;

  return (
    <div className="px-4 pt-5 pb-10" data-testid="worker-jobboard-page">
      <div className="flex items-center gap-2">
        <Briefcase className="h-5 w-5 text-[#00c86e]" />
        <h1 className="text-2xl font-bold text-white tracking-tight font-brand">Job Board</h1>
      </div>
      <p className="text-xs text-slate-400 mt-1 mb-5">Open shifts you can pick up — apply and the office will confirm</p>

      {jobs.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-white/5 p-8 text-center text-slate-400 text-sm" data-testid="worker-jobboard-empty">
          No open shifts right now. Check back soon.
        </div>
      ) : (
        <div className="space-y-2.5">
          {jobs.map((s) => (
            <div key={s.name} className="rounded-2xl border border-white/10 bg-white/5 p-4" data-testid={`worker-job-${s.name}`}>
              <div className="flex items-center justify-between">
                <div className="text-white font-semibold text-[15px]">{dayLabel(s.shift_date)}</div>
                <div className="flex items-center gap-1.5 text-[#00c86e] text-sm font-medium">
                  <Clock className="h-3.5 w-3.5" /> {fmtTime(s.linked ? s.package_start : s.start_time)}–{fmtTime(s.linked ? s.package_end : s.end_time)}
                </div>
              </div>
              <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                <span className="text-white/90 text-sm font-medium">{s.limited ? (s.participant_first_name || "Participant") : s.participant_name}</span>
                {s.linked ? <span className="text-[9px] font-semibold uppercase tracking-wide text-indigo-200 bg-indigo-500/20 border border-indigo-400/30 rounded px-1.5 py-0.5 flex items-center gap-1" data-testid={`worker-job-linked-${s.name}`}><Link2 className="h-2.5 w-2.5" /> Linked · {s.leg_count} shifts</span> : null}
                {s.is_sleepover ? <span className="text-[9px] font-semibold uppercase tracking-wide text-indigo-200 bg-indigo-500/20 border border-indigo-400/30 rounded px-1.5 py-0.5" data-testid={`worker-job-sleepover-${s.name}`}>Sleepover</span> : null}
              </div>
              {s.limited ? (
                s.suburb && <div className="mt-1 flex items-center gap-1 text-[11px] text-slate-400" data-testid={`worker-job-suburb-${s.name}`}><MapPin className="h-3 w-3" /> {s.suburb}</div>
              ) : (
                s.location && <div className="mt-1 flex items-center gap-1 text-[11px] text-slate-400"><MapPin className="h-3 w-3" /> {s.location}</div>
              )}
              <div className="mt-1 text-[11px] text-slate-400">{s.linked ? `${s.package_hours}h total · one worker covers all ${s.leg_count} legs` : `${s.rostered_hours}h shift`}</div>
              <div className="mt-1 flex items-center gap-1 text-[11px] text-slate-400" data-testid={`worker-job-distance-${s.name}`}>
                <MapPin className="h-3 w-3" /> {s.distance_km != null ? `~${s.distance_km} km from home` : "Distance unavailable"}
              </div>
              {s.limited && (
                <div className="mt-2 flex items-center gap-1 text-[10px] text-slate-500" data-testid={`worker-job-limited-${s.name}`}>
                  <Lock className="h-2.5 w-2.5" /> Limited details — full information is shared if you're picked for this shift
                </div>
              )}
              {(s.clashes || []).length > 0 && (
                <div className="mt-2 rounded-md border border-red-500/30 bg-red-500/10 px-2 py-1.5 text-[11px] text-red-200" data-testid={`worker-job-clash-${s.name}`}>
                  <AlertTriangle className="h-3 w-3 inline mr-1 -mt-px" />
                  {s.clashes.map((c, i) => (
                    <span key={i}>
                      {i > 0 && " · "}
                      You're already rostered {fmtTime(c.start_time)}–{fmtTime(c.end_time)} that day
                      {c.participant_name ? ` (${String(c.participant_name).split(" ")[0]})` : ""}
                      {c.kind === "travel" ? ` — only ${c.gap_min} min between them and about ${c.drive_min} min of travel`
                        : c.kind === "unknown_distance" ? ` — only ${c.gap_min} min between them` : ""}
                    </span>
                  ))}
                </div>
              )}
              {s.removed_by_office && (
                <div className="mt-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-2 py-1.5 text-[11px] text-amber-200" data-testid={`worker-job-removed-${s.name}`}>
                  Your application was removed by the office: “{s.removed_by_office.reason}”
                </div>
              )}
              <div className="mt-3 flex gap-2">
                {s.applied ? (<>
                  <div className="flex-1 h-9 rounded-md bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-xs font-semibold flex items-center justify-center gap-1" data-testid={`worker-job-applied-${s.name}`}>
                    <Check className="h-3.5 w-3.5" /> Applied
                  </div>
                  <button onClick={() => withdraw(s.name)} disabled={applying === s.name} data-testid={`worker-job-withdraw-${s.name}`}
                    className="h-9 px-3 rounded-md border border-white/15 text-slate-300 hover:bg-white/10 text-xs font-semibold flex items-center justify-center gap-1 disabled:opacity-60">
                    {applying === s.name ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Withdraw"}
                  </button>
                </>) : (
                  <button onClick={() => requestApply(s)} disabled={applying === s.name} data-testid={`worker-job-apply-${s.name}`}
                    className="flex-1 h-9 rounded-md bg-brand hover:bg-brand-dark text-white text-xs font-semibold flex items-center justify-center gap-1 disabled:opacity-60">
                    {applying === s.name ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Send className="h-3.5 w-3.5" /> Apply</>}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {clashJob && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 p-4" data-testid="worker-job-clash-dialog" onClick={() => setClashJob(null)}>
          <div className="w-full max-w-sm rounded-2xl bg-[#1a1d24] border border-white/10 p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 text-red-300">
              <AlertTriangle className="h-5 w-5" />
              <div className="font-bold text-[15px]">You're already rostered that day</div>
            </div>
            <div className="mt-3 space-y-1.5 text-[13px] text-slate-300 leading-relaxed">
              {(clashJob.clashes || []).map((c, i) => (
                <div key={i}>
                  <b className="text-white">{fmtTime(c.start_time)}–{fmtTime(c.end_time)}</b>
                  {c.participant_name ? ` with ${String(c.participant_name).split(" ")[0]}` : ""}
                  {c.kind === "travel" ? ` — only ${c.gap_min} min gap and about ${c.drive_min} min of travel`
                    : c.kind === "unknown_distance" ? ` — only a ${c.gap_min} min gap before this one`
                    : " — this overlaps the shift you're applying for"}
                </div>
              ))}
              <div className="pt-1 text-slate-400">
                You can still apply — the office will see the clash and decide.
              </div>
            </div>
            <div className="mt-5 flex gap-2">
              <button onClick={() => setClashJob(null)} data-testid="worker-job-clash-cancel"
                className="flex-1 h-10 rounded-xl border border-white/15 text-slate-300 text-sm font-semibold">Cancel</button>
              <button onClick={() => afterClash(clashJob)} data-testid="worker-job-clash-continue"
                className="flex-1 h-10 rounded-xl bg-brand text-white text-sm font-semibold">Apply anyway</button>
            </div>
          </div>
        </div>
      )}

      {confirmJob && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 p-4" data-testid="worker-job-commit-dialog" onClick={() => setConfirmJob(null)}>
          <div className="w-full max-w-sm rounded-2xl bg-[#1a1d24] border border-white/10 p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 text-amber-300">
              <AlertTriangle className="h-5 w-5" />
              <div className="font-bold text-[15px]">Less than 30 days' notice</div>
            </div>
            <p className="mt-3 text-[13px] text-slate-300 leading-relaxed">
              This shift is on <b className="text-white">{dayLabel(confirmJob.shift_date)}</b> — only <b className="text-white">{daysUntil(confirmJob.shift_date)} day(s)</b> away.
              If you accept it now, <b className="text-white">you're committing to attend</b> and will be expected to work it.
            </p>
            <div className="mt-5 flex gap-2">
              <button onClick={() => setConfirmJob(null)} data-testid="worker-job-commit-cancel"
                className="flex-1 h-10 rounded-md border border-white/15 text-slate-200 text-sm font-semibold">Cancel</button>
              <button onClick={() => apply(confirmJob.name)} data-testid="worker-job-commit-confirm"
                className="flex-1 h-10 rounded-md bg-brand hover:bg-brand-dark text-white text-sm font-semibold">I understand — apply</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
